import { CaretDown, Heartbeat } from "@phosphor-icons/react";
import type {
  ArmPerformanceResponse,
  HeartbeatWhyResponse,
  WhoamiResponse,
} from "./demo-control-model";

const buttonBase =
  "rounded-md border px-2 py-1.5 text-[10.5px] font-medium transition-all disabled:cursor-not-allowed disabled:opacity-55";

export function HeartbeatReadoutSection({
  data,
  heartbeatWhy,
  arms,
  loading,
  onRefresh,
}: {
  data: WhoamiResponse | undefined;
  heartbeatWhy: HeartbeatWhyResponse | undefined;
  arms: ArmPerformanceResponse | undefined;
  loading: boolean;
  onRefresh: () => void;
}) {
  const decision = heartbeatWhy?.decision ?? null;
  const story = describeHeartbeatDecision(decision);
  const evidence = decision ? summarizeHeartbeatEvidence(decision).slice(0, 3) : [];
  const watching = decision ? summarizeWatchedSignals(decision).slice(0, 3) : [];
  const rawTrace = decision ? formatRawTrace(decision) : "";

  // shrink-0: overflow-hidden zeroes min-height in the flex column, and the
  // flex algorithm would squash this section flat instead of scrolling.
  return (
    <section className="relative shrink-0 overflow-hidden rounded-xl border border-navy/10 bg-[radial-gradient(circle_at_12%_4%,rgba(20,184,166,0.16),transparent_30%),linear-gradient(180deg,rgba(250,253,252,0.97),rgba(244,248,247,0.94))] p-3 shadow-[0_16px_36px_rgba(28,45,54,0.08)]">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2.5">
          <span className="relative mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-full bg-navy text-white shadow-sm">
            <span className="absolute h-full w-full rounded-full bg-teal-300/45 motion-safe:animate-ping" />
            <Heartbeat size={16} weight="fill" className="relative text-teal-100" />
          </span>
          <div className="min-w-0">
            <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-teal-800">
              Live Heartbeat
            </p>
            <p className="mt-0.5 text-wrap font-semibold text-[14px] leading-snug text-navy">
              {loading ? "Reading latest decision..." : story.headline}
            </p>
            <p className="mt-1 text-[10.5px] leading-snug text-gray-600">{story.subhead}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={!data?.userId || loading}
          className={`${buttonBase} shrink-0 border-navy/10 bg-white/75 text-teal-800 hover:border-teal-500/45 hover:bg-white disabled:bg-white/40`}
        >
          {loading ? "..." : "Refresh"}
        </button>
      </div>
      {decision ? (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-navy/10 bg-white/70 px-3 py-2.5">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-mono text-[8px] uppercase tracking-[0.13em] text-gray-500">
                  Decision
                </p>
                <p className="mt-1 truncate font-semibold text-[12.5px] text-navy">
                  {story.actionLabel}
                </p>
              </div>
              <ReadoutPill label={story.outcomeLabel} tone={story.outcomeTone} />
            </div>
            <p className="mt-2 text-[11px] leading-snug text-gray-700">{story.rationale}</p>
          </div>

          <div className="grid grid-cols-3 gap-1.5">
            <MiniMetric label="Window" value={formatWindowLabel(decision.window)} />
            <MiniMetric label="Mode" value={decision.llmInvoked ? "Model" : "Rule"} />
            <MiniMetric
              label="Cohort"
              value={decision.cohort === "treatment" ? "Live" : "Holdout"}
            />
          </div>

          {watching.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {watching.map((signal) => (
                <span
                  key={signal}
                  className="rounded-full border border-navy/10 bg-white/65 px-2 py-1 text-[10px] font-medium text-gray-700"
                >
                  {signal}
                </span>
              ))}
            </div>
          ) : null}

          {evidence.length > 0 ? (
            <div className="space-y-1.5">
              {evidence.map((item) => (
                <div key={`${item.label}-${item.text}`} className="grid grid-cols-[72px_1fr] gap-2">
                  <p className="font-mono text-[8px] uppercase tracking-[0.12em] text-gray-500">
                    {item.label}
                  </p>
                  <p className="text-[10.5px] leading-snug text-gray-700">{item.text}</p>
                </div>
              ))}
            </div>
          ) : null}

          {rawTrace ? (
            <details className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between rounded-md border border-transparent px-1 py-1 text-[10px] font-medium text-gray-500 transition-colors hover:border-navy/10 hover:bg-white/50">
                Raw trace
                <CaretDown
                  size={12}
                  weight="bold"
                  className="transition-transform group-open:rotate-180"
                />
              </summary>
              <pre className="mt-1.5 max-h-32 overflow-auto rounded-md bg-navy/90 p-2 text-[9.5px] leading-snug text-teal-50">
                {rawTrace}
              </pre>
            </details>
          ) : null}
        </div>
      ) : (
        <p className="mt-3 rounded-lg border border-dashed border-navy/15 bg-white/50 px-3 py-2 text-[11px] leading-snug text-gray-600">
          Run Signal story or HB tick and the latest decision will appear here.
        </p>
      )}
      {arms && arms.arms.length > 0 ? <ArmTable performance={arms} /> : null}
    </section>
  );
}

// Above this share of sends, the library is too narrow to find anything else to say.
const REENGAGE_SHARE_CEILING = 0.15;

function ArmTable({ performance }: { performance: ArmPerformanceResponse }) {
  const rules = [...new Set(performance.arms.map((a) => a.rule))];
  const cell = (rule: string, assignment: string) =>
    performance.arms.find((a) => a.rule === rule && a.assignment === assignment);
  const rows = rules
    .map((rule) => ({ rule, send: cell(rule, "send"), hold: cell(rule, "hold") }))
    .sort((a, b) => (b.send?.n ?? 0) - (a.send?.n ?? 0));
  const share = (n: number | undefined) =>
    performance.delivered > 0 && n ? n / performance.delivered : 0;
  const fmt = (r: number | null | undefined) =>
    r === null || r === undefined ? "-" : r.toFixed(2);
  const count = (c: { n: number; scored: number } | undefined) =>
    c ? `${c.n}${c.scored < c.n ? ` (${c.scored})` : ""}` : "0";
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-navy/10 bg-white/70 px-3 py-2.5">
      <p className="font-mono text-2xs uppercase tracking-[0.13em] text-gray-500">
        Arms · {performance.delivered} sent in 30 days
      </p>
      <table className="mt-1.5 w-full text-xs text-gray-700">
        <thead className="font-mono text-2xs uppercase tracking-[0.1em] text-gray-400">
          <tr>
            <th className="pr-2 text-left font-normal">Arm</th>
            <th className="px-1 text-right font-normal">Sent</th>
            <th className="px-1 text-right font-normal">Held</th>
            <th className="px-1 text-right font-normal">Reward S/H</th>
            <th className="pl-1 text-right font-normal">Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ rule, send, hold }) => {
            const s = share(send?.n);
            const crowded = rule === "rule.reengage" && s > REENGAGE_SHARE_CEILING;
            return (
              <tr key={rule} className={crowded ? "text-amber-700" : undefined}>
                <td className="pr-2 font-medium">{rule.replace(/^rule\./u, "")}</td>
                <td className="px-1 text-right tabular-nums">{count(send)}</td>
                <td className="px-1 text-right tabular-nums">{count(hold)}</td>
                <td className="px-1 text-right tabular-nums">
                  {fmt(send?.reward)} / {fmt(hold?.reward)}
                </td>
                <td className="pl-1 text-right tabular-nums">{Math.round(s * 100)}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="mt-1 text-2xs text-gray-500">Bracketed counts are the rows that scored.</p>
    </div>
  );
}

type HeartbeatDecision = NonNullable<HeartbeatWhyResponse["decision"]>;
type ReadoutTone = "brand" | "ok" | "muted" | "warn";

const ACTION_COPY: Record<
  string,
  {
    headline: string;
    subhead: string;
    outcomeLabel: string;
    outcomeTone: ReadoutTone;
    fallback: string;
  }
> = {};

function ReadoutPill({ label, tone }: { label: string; tone: ReadoutTone }) {
  const toneClass =
    tone === "brand"
      ? "border-teal-500/40 bg-teal-100/60 text-teal-800"
      : tone === "ok"
        ? "border-leaf/40 bg-leaf-soft/70 text-leaf-dark"
        : tone === "warn"
          ? "border-amber-400/45 bg-amber-50 text-amber-700"
          : "border-white/50 bg-white/60 text-gray-600";
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.08em] ${toneClass}`}
    >
      {label}
    </span>
  );
}

function MiniMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-navy/10 bg-white/60 px-2 py-1.5">
      <p className="font-mono text-[7.5px] uppercase tracking-[0.12em] text-gray-400">{label}</p>
      <p className="mt-0.5 truncate text-[10.5px] font-semibold text-navy">{value}</p>
    </div>
  );
}

function describeHeartbeatDecision(decision: HeartbeatDecision | null): {
  headline: string;
  subhead: string;
  actionLabel: string;
  outcomeLabel: string;
  outcomeTone: ReadoutTone;
  rationale: string;
} {
  if (!decision) {
    return {
      headline: "Waiting for the next tick",
      subhead: "No heartbeat decision has been recorded for this member yet.",
      actionLabel: "No action yet",
      outcomeLabel: "Idle",
      outcomeTone: "muted",
      rationale: "Run the signal story to create a fresh decision for the demo.",
    };
  }

  const actionLabel = formatActionLabel(decision.agentAction);
  const quietHold = isQuietHold(decision);
  const copy = ACTION_COPY[decision.agentAction];
  if (copy) {
    return {
      ...copy,
      actionLabel,
      outcomeLabel: copy.outcomeLabel,
      rationale: conciseRationale(decision.agentRationale, copy.fallback),
    };
  }

  if (quietHold) {
    return {
      headline: "Held for quiet hours",
      subhead: "The plan can move without interrupting sleep.",
      actionLabel,
      outcomeLabel: "Held",
      outcomeTone: "warn",
      rationale:
        "Quiet hours are active, so member-facing prompts stay held unless something urgent needs attention.",
    };
  }

  if (decision.fired) {
    return {
      headline: "Decision fired",
      subhead: "A useful surface is ready for this moment.",
      actionLabel,
      outcomeLabel: "Sent",
      outcomeTone: "ok",
      rationale: conciseRationale(
        decision.agentRationale,
        "The latest signals are specific enough to justify one member-facing action.",
      ),
    };
  }

  return {
    headline: "No surface change",
    subhead: "Heartbeat evaluated the context and chose not to interrupt.",
    actionLabel,
    outcomeLabel: "No-op",
    outcomeTone: "muted",
    rationale: conciseRationale(
      decision.agentRationale,
      "The context was not strong enough to justify a new prompt.",
    ),
  };
}

function summarizeHeartbeatEvidence(
  decision: HeartbeatDecision,
): Array<{ label: string; text: string }> {
  if (decision.lensTrace.length === 0) {
    return [{ label: "Reason", text: "No lens trace was captured for this tick." }];
  }
  return decision.lensTrace.map((item) => {
    const lens = item.lens ?? "Lens";
    return {
      label: formatLensLabel(lens),
      text: summarizeLensNarrative(item.narrative),
    };
  });
}

function summarizeWatchedSignals(decision: HeartbeatDecision): string[] {
  const signals = decision.decisionFrame?.candidateSignals;
  if (!Array.isArray(signals)) return [];
  return signals
    .filter((signal): signal is string => typeof signal === "string")
    .map(formatSignalName);
}

function formatActionLabel(action: string): string {
  const labels: Record<string, string> = {};
  return labels[action] ?? humanizeSlug(action);
}

function formatLensLabel(lens: string): string {
  const normalized = lens.toLowerCase();
  if (normalized.includes("glp1")) return "GLP-1";
  if (normalized.includes("calendar")) return "Calendar";
  if (normalized.includes("thrive")) return "Thrive";
  if (normalized.includes("persona")) return "Persona";
  if (normalized.includes("safety")) return "Safety";
  if (normalized.includes("time")) return "Timing";
  return lens.replace(/Lens$/u, "");
}

/**
 * The lens's own narrative, trimmed to fit the panel.
 *
 * It used to substitute a canned sentence per lens and throw the real narrative
 * away, which made a debugging panel state things that were not true: on a
 * member whose active lens was Mental Health Foundations, and whose own header
 * said so, the persona row read "The GLP-1 lens is active, with emphasis on
 * dose support and side-effect management." The point of this panel is to show
 * what the engine actually decided, so it shows what the engine actually said.
 */
function summarizeLensNarrative(narrative: string | undefined): string {
  return trimSentence(stripTracePrefix(narrative) || "No narrative captured.", 118);
}

function conciseRationale(rationale: string | null, fallback: string): string {
  return trimSentence(stripTracePrefix(rationale ?? fallback), 178);
}

function stripTracePrefix(value: string | undefined): string {
  return (value ?? "")
    .replace(/^\[[^\]]+\]\s*/u, "")
    .replace(/\s+/gu, " ")
    .trim();
}

function trimSentence(value: string, limit: number): string {
  if (value.length <= limit) return value;
  const clipped = value.slice(0, limit - 1).trimEnd();
  const sentenceEnd = Math.max(clipped.lastIndexOf("."), clipped.lastIndexOf("?"));
  if (sentenceEnd > 48) return clipped.slice(0, sentenceEnd + 1);
  return `${clipped}...`;
}

function isQuietHold(decision: HeartbeatDecision): boolean {
  const reason =
    `${decision.shortCircuitReason ?? ""} ${decision.agentRationale ?? ""}`.toLowerCase();
  return reason.includes("quiet") || reason.includes("holding off");
}

function formatWindowLabel(window: string | null): string {
  return window ? humanizeSlug(window) : "Now";
}

function formatSignalName(signal: string): string {
  const labels: Record<string, string> = {
    "adherence.slippage_detected": "Engagement gap",
    "hydration.ml": "Hydration low",
    "nutrition.meal": "Protein window",
    "calendar.event": "Packed calendar",
    "medication.dose": "Dose signal",
    "user.feedback": "Member feedback",
    "user.microstep_feedback": "Feedback logged",
    "user.notification_dismissed": "Dismissed nudges",
  };
  return labels[signal] ?? humanizeSlug(signal);
}

function humanizeSlug(value: string): string {
  return value
    .replace(/[._-]+/gu, " ")
    .replace(/\b\w/gu, (char) => char.toUpperCase())
    .trim();
}

function formatRawTrace(decision: HeartbeatDecision): string {
  const rows = decision.lensTrace.map((item) => {
    const label = item.lens ?? "lens";
    const narrative = stripTracePrefix(item.narrative) || "No narrative captured.";
    return `${label}: ${narrative}`;
  });
  return rows.join("\n");
}
